/* eslint-disable camelcase */
/* eslint-disable react/jsx-no-undef */
/* eslint-disable promise/always-return */
import { API } from '../../api';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  notification,
} from 'antd';
import { displayFileSize, handleError, sendErrorNotification } from '../../utils/util';
import React, { useEffect, useState } from 'react';

const { RangePicker } = DatePicker;

// Columns used for DIS Files table
const columns = [
  {
    title: 'File',
    dataIndex: 'key',
    key: 'key',
    defaultSortOrder: 'ascend',
    sorter: (a, b) => {
      if (a.key === null || b.key === null) return 1;
      return a.key.localeCompare(b.key);
    },
  },
  {
    title: 'Size',
    dataIndex: 'size',
    key: 'size',
    sorter: (a, b) => a.size - b.size,
    render: size => displayFileSize(size),
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    key: 'created_at',
    sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    render: created_at => new Date(created_at).toLocaleString(),
  },
  {
    title: 'Updated At',
    dataIndex: 'updated_at',
    key: 'updated_at',
    sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    render: updated_at => new Date(updated_at).toLocaleString(),
  },
  {
    title: 'Last Modified',
    dataIndex: 'last_modified',
    key: 'last_modified',
    sorter: (a, b) => new Date(a.last_modified).getTime() - new Date(b.last_modified).getTime(),
    render: last_modified => new Date(last_modified).toLocaleString(),
  },
  {
    title: 'Deleted At',
    dataIndex: 'deleted_at',
    key: 'deleted_at',
    sorter: (a, b) => new Date(a.deleted_at).getTime() - new Date(b.deleted_at).getTime(),
    render: deleted_at => (deleted_at ? new Date(deleted_at).toLocaleString() : ''),
  },
];

/**
 * Dashboard showing information on data ingestion via communication
 * with the data ingest service.
 * Views for:
 * 1. Scheduling ingestion jobs
 * 2. Showing uploaded files
 * 3. Showing ingestion progress
 */

// TODO: REFACTOR TO NOT NEED CLIENT URL. LET BACKEND FETCH FROM DB BEFORE MAKING REQUEST TO DIS
const FilesView = props => {
  const { clientDisApi, selectedClient } = props;
  const [form] = Form.useForm();
  const [filtersForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesAll, setFilesAll] = useState([]);
  const [createdAtFilter, setCreatedAtFilter] = useState([]);
  const [updatedAtFilter, setUpdatedAtFilter] = useState([]);
  const [modifiedAtFilter, setModifiedAtFilter] = useState([]);
  const [deletedAtFilter, setDeletedAtFilter] = useState([]);
  const [deletedFilter, setDeletedFilter] = useState(null);
  const [fileSizePrefix, setFileSizePrefix] = useState('');
  const [fileSizeReadable, setFileSizeReadable] = useState('mb');
  const [fileSize, setFileSize] = useState(0);
  const selectBefore = (
    <Select
      defaultValue=""
      style={{
        width: 130,
      }}
      onChange={setFileSizePrefix}
    >
      <Option value="gt" data-cy="select_gt">
        Greater Than
      </Option>
      <Option value="lt">Less Than</Option>
    </Select>
  );
  const selectAfter = (
    <Select
      defaultValue="mb"
      style={{
        width: 75,
      }}
      onChange={setFileSizeReadable}
    >
      <Option value="kb">KB</Option>
      <Option value="mb" data-cy="select_mb">
        MB
      </Option>
      <Option value="gb">GB</Option>
    </Select>
  );

  const getDateRangeQueryParam = (name, range) => {
    return `${name}.gt=${range[0]}T00:00:00.000Z&${name}.lt=${range[1]}T23:59:59.000Z&`;
  };

  /**
   * Sets DIS API files endpoint GET req params
   * Queries DIS API with/without filters set
   */
  const onFinish = async () => {
    setLoading(true);

    let endpoint = '/v1/files/s3?';

    if (createdAtFilter.length === 2) {
      endpoint += getDateRangeQueryParam('created', createdAtFilter);
    }

    if (updatedAtFilter.length === 2) {
      endpoint += getDateRangeQueryParam('updated', updatedAtFilter);
    }

    if (modifiedAtFilter.length === 2) {
      endpoint += getDateRangeQueryParam('modified', modifiedAtFilter);
    }

    if (deletedAtFilter.length === 2) {
      endpoint += getDateRangeQueryParam('deleted', deletedAtFilter);
    }

    if (fileSize > 0) {
      let bytes = fileSize;

      if (fileSizeReadable === 'mb') {
        bytes *= 1024 ** 2;
      } else if (fileSizeReadable === 'gb') {
        bytes *= 1024 ** 3;
      } else if (fileSizeReadable === 'kb') {
        bytes *= 1024 ** 1;
      }

      endpoint += `size.${fileSizePrefix}=${bytes}&`;
    }

    if (deletedFilter !== null) {
      endpoint += `includeDeleted=${deletedFilter}&`;
    }

    const body = {
      dis_api_url: clientDisApi,
      client_id: selectedClient.id,
      endpoint,
    };

    await API.get(`/client/${selectedClient.id}`)
      .then(res => {
        body.dis_api_url = res.data.data_ingest_api_url;
      })
      .catch(e => {
        notification.error({
          message: 'ERROR: ',
          description: `DIMA Failed to retrieve updated client information with the following error: ${e}`,
          duration: 6,
          placement: 'topLeft',
        });
      });

    await API.post('/dis/files', body)
      .then(res => {
        const filteredRes = res.data.results.filter(row => parseInt(row.size, 10) > 0);
        setFiles(filteredRes);
        setFilesAll(res.data.results);
      })
      .catch(error => {
        handleError(error);
      });
    setLoading(false);
  };

  /**
   * Sends request after filters are applied
   * Resets form fields after request is sent
   */
  const onSetFilters = () => {
    onFinish();
    setModifiedAtFilter([]);
    setCreatedAtFilter([]);
    setUpdatedAtFilter([]);
    setDeletedAtFilter([]);
    setFileSize(0);
    setDeletedFilter(null);
    filtersForm.resetFields();
    setShowFilters(false);
  };

  /**
   * Sends error notification
   * @param {errorInfo} errorInfo
   */
  const onFinishFailed = errorInfo => {
    sendErrorNotification(errorInfo);
  };

  /**
   * Callback function for Mappings search / filter.
   */
  const searchFiles = e => {
    const filesList = [];

    for (const i in filesAll) {
      if (
        filesAll[i].key.toLowerCase().includes(e.target.value.toLowerCase()) &&
        parseInt(filesAll[i].size, 10) > 0
      ) {
        filesList.push(filesAll[i]);
      }
    }

    setFiles(filesList);
  };

  // the tabs will be the separate components that make up the dashboard
  const filesTable = (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'left',
          alignItems: 'center',
        }}
      >
        <Form
          name="basic"
          layout="inline"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item>
            <Button
              type="primary"
              htmlType="button"
              data-cy="dis-filter-search-btn"
              onClick={e => [e.stopPropagation(), setShowFilters(true)]}
            >
              Filters
            </Button>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" data-cy="dis-search-btn" loading={loading}>
              Search
            </Button>
          </Form.Item>
        </Form>
        {filesAll.length > 0 && (
          <>
            <Input
              data-cy="search-files"
              style={{ width: '350px', justifyContent: 'right', marginRight: '20px' }}
              addonBefore="File Search:"
              onChange={searchFiles}
            />
            <Space size={[0, 8]} wrap>
              <Tag color="cyan"> {files.length} Files Found</Tag>
            </Space>
          </>
        )}
      </div>
      <br />
      <Table
        rowClassName={record => (record.deleted_at === null ? 'table-row-light' : 'table-row-dark')}
        dataSource={files}
        columns={columns}
        pagination={{ pageSize: 100 }}
        loading={loading}
      />
    </>
  );

  // Updates DatePicker field values
  useEffect(() => {
    filtersForm.setFieldsValue({
      updated_at_filter: updatedAtFilter,
      created_at_filter: createdAtFilter,
      modified_at_filter: modifiedAtFilter,
      deleted_at_filter: deletedAtFilter,
    });
  }, [form]);

  return (
    <>
      <Modal
        title="File Filters"
        visible={showFilters}
        onOk={onSetFilters}
        onCancel={e => [e.stopPropagation(), setShowFilters(false)]}
        footer={null}
      >
        <Form
          form={filtersForm}
          name="basic"
          layout="horizontal"
          onFinish={onSetFilters}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item name="created_at_filter" label="Created At">
            <Space direction="vertical" size={12}>
              <RangePicker
                style={{ width: '350px' }}
                onChange={(value, dateString) => [setCreatedAtFilter(dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item name="updated_at_filter" label="Updated At">
            <Space direction="vertical" size={12}>
              <RangePicker
                style={{ width: '350px' }}
                onChange={(value, dateString) => [setUpdatedAtFilter(dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item name="modified_at_filter" label="Modified At">
            <Space direction="vertical" size={12}>
              <RangePicker
                style={{ width: '350px' }}
                onChange={(value, dateString) => [setModifiedAtFilter(dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item name="deleted_at_filter" label="Deleted At">
            <Space direction="vertical" size={12}>
              <RangePicker
                style={{ width: '350px' }}
                onChange={(value, dateString) => [setDeletedAtFilter(dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item name="deleted_filter" label="Include Deleted">
            <Select
              defaultValue=""
              style={{
                width: 322,
              }}
              onChange={setDeletedFilter}
            >
              <Option value="true" data-cy="select_true">
                True
              </Option>
              <Option value="false">False</Option>
            </Select>
          </Form.Item>
          <Form.Item name="file_size_filter" label="File Size">
            <InputNumber
              addonBefore={selectBefore}
              addonAfter={selectAfter}
              defaultValue={null}
              onChange={value => setFileSize(value)}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              data-cy="dis-apply-filter-btn"
              loading={loading}
              style={{ float: 'right' }}
            >
              Apply Filters
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {filesTable}
    </>
  );
};

export default FilesView;
